/**
 * Web-specific useAuthDialog hook
 * Manages authentication dialog state and handles login/register flows
 */

import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, setUser, setWallets, authService, type LoginRequest, type RegisterRequest, type SocialLoginRequest, type ApiError, getUserProfile, saveAccessToken, saveRefreshToken, setShowOnboarding } from '@ideoz/core';
import { useGoogleAuth } from './useGoogleAuth';
import { GOOGLE_AUTH_CONFIG } from '../config/googleAuth';

export interface UseAuthDialogReturn {
  isOpen: boolean;
  mode: 'login' | 'register';
  isLoading: boolean;
  error?: string;
  openRegister: (opts?: { fromChat?: boolean }) => void;
  openLogin: () => void;
  closeDialog: () => void;
  switchMode: (mode: 'login' | 'register') => void;
  handleLogin: (email: string, password: string, opts?: { pairingKey?: string | null; figmaSource?: string | null }) => Promise<void>;
  handleRegister: (name: string, email: string, password: string, opts?: { fromChat?: boolean; pairingKey?: string | null; figmaSource?: string | null }) => Promise<void>;
  handleGoogleAuth: (opts?: { pairingKey?: string | null; figmaSource?: string | null }) => Promise<void>;
  handleNavigateToForgotPassword: () => void;
  googleIsLoaded: boolean;
  onboardingSource?: 'homepage' | 'chat';
  setOnboardingSource?: (src: 'homepage' | 'chat' | undefined) => void;
}

export const useAuthDialog = (): UseAuthDialogReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [onboardingSource, setOnboardingSource] = useState<'homepage' | 'chat' | undefined>(undefined);
  const pluginRedirectParamsRef = useRef<{ pairingKey?: string | null; figmaSource?: string | null }>({});
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Initialize Google Auth
  const {
    isLoaded: googleIsLoaded,
    isLoading: googleIsLoading,
    signInWithGoogle,
    error: googleError,
  } = useGoogleAuth({
    clientId: GOOGLE_AUTH_CONFIG.CLIENT_ID,
    onSuccess: async (user, credential) => {
      setGoogleLoading(true);
      console.log('useGoogleAuth onSuccess callback triggered');
      console.log('Current pluginRedirectParamsRef:', pluginRedirectParamsRef.current);
      
      // Also check URL for pairing_key as fallback (for cases where Google FedCM/One Tap
      // bypasses the button click and handleGoogleAuth is never called)
      const urlParams = new URLSearchParams(window.location.search);
      const urlPairingKey = urlParams.get('pairing_key');
      const urlFigmaSource = urlParams.get('figma_source');
      
      // Use ref value if available, otherwise fall back to URL params
      const pairingKey = pluginRedirectParamsRef.current.pairingKey || urlPairingKey;
      const figmaSource = pluginRedirectParamsRef.current.figmaSource || urlFigmaSource;
      
      console.log('Resolved pairing params - pairingKey:', pairingKey, 'figmaSource:', figmaSource);
      
      try {
        // Call backend social login API
        const socialLoginData: SocialLoginRequest = {
          id_token: credential, // Google ID token (JWT)
        };

        // Add pairing_key if provided
        if (pairingKey) {
          socialLoginData.pairing_key = pairingKey;
          console.log('Added pairing_key to socialLoginData');
        }

        const response = await authService.socialLogin(socialLoginData);

        if (!response.access_token) {
          throw new Error(response.message || 'Invalid social login response from server');
        }

        saveAccessToken(response.access_token);
        saveRefreshToken(response.refresh_token);
        (window as any).appAccessToken = response.access_token;
        const accessToken = response.access_token;

        // Update Redux state with user data and check onboarding status
        if (accessToken) {
          const userData = await dispatch(getUserProfile(accessToken)).unwrap();

          // Check if coming from Figma plugin
          // The pairing_key is the critical parameter for plugin authentication
          console.log('Checking pairingKey for redirect:', pairingKey);
          if (pairingKey) {
            console.log('Redirecting to /redirect with pairing_key');
            closeDialog();
            navigate(`/redirect?pairing_key=${pairingKey}&figma_source=${figmaSource || 'desktop'}`);
            setGoogleLoading(false);
            return;
          }

          // Check if onboarding should be shown based on metadata.isOnBoardingShown flag
          const pathname = window.location.pathname;
          const isOnLandingPage = pathname === '/' || pathname === '/landing';
          // Check both camelCase and snake_case, and handle string values
          const isOnBoardingShownRaw = userData?.metadata?.isOnBoardingShown ??
            userData?.metadata?.is_onboarding_shown ??
            false;
          const isOnBoardingShown = typeof isOnBoardingShownRaw === 'string'
            ? isOnBoardingShownRaw.toLowerCase() === 'true'
            : Boolean(isOnBoardingShownRaw);

          // Only show onboarding if first-time user on landing page
          if (isOnBoardingShown === false && isOnLandingPage) {
            dispatch(setShowOnboarding(true));
            navigate('/onboarding');
          } else {
            // All other cases - navigate to home page
            // Update onboarding flag if needed
            if (isOnBoardingShown === false) {
              authService.updateOnboardingShown().catch((error) => {
                console.error('Failed to update onboarding shown flag:', error);
              });
            }
            // Don't redirect if user is on conversation page or came from conversation
            if (handleConversationRedirect()) {
              return;
            }
            navigate('/');
          }
        }

        closeDialog();
      } catch (error) {
        console.error('Social login failed:', error);
        const apiError = error as ApiError;
        throw new Error(`Google sign-in failed: ${apiError.message || 'Unknown error'}`);
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: (error) => {
      console.error('Google authentication failed:', error);
      // Error will be handled by the component
    },
  });

  const openLogin = useCallback(() => {
    setMode('login');
    setIsOpen(true);
    setOnboardingSource(undefined);
  }, []);

  // Accepts an option to indicate if registration is from chat or homepage
  // Only homepage registration should trigger onboarding. All chat-based registrations (including create project from chat) should set onboardingSource to 'chat'.
  const openRegister = useCallback((opts?: { fromChat?: boolean }) => {
    setMode('register');
    setIsOpen(true);
    if (opts?.fromChat) {
      setOnboardingSource('chat');
    } else if (window.location.pathname === '/' || window.location.pathname === '/landing') {
      setOnboardingSource('homepage');
    } else {
      setOnboardingSource('chat');
    }
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setIsLoading(false);
  }, []);

  // Helper function to handle conversation page redirect logic
  const handleConversationRedirect = useCallback(() => {
    const pathname = window.location.pathname;
    const referrer = document.referrer;
    const isOnConversation = pathname === '/conversation' || pathname.startsWith('/conversation/');
    const cameFromConversation = referrer.includes('/conversation');
    
    if (isOnConversation) {
      closeDialog();
      return true; // Indicates we handled the redirect
    }
    if (cameFromConversation) {
      // Extract conversation path from referrer or navigate to base conversation
      const conversationMatch = referrer.match(/\/conversation[^?]*/);
      if (conversationMatch) {
        closeDialog();
        navigate(conversationMatch[0]);
        return true;
      }
      closeDialog();
      navigate('/conversation');
      return true;
    }
    return false; // Indicates we didn't handle redirect, caller should navigate to '/'
  }, [navigate, closeDialog]);

  const switchMode = useCallback((newMode: 'login' | 'register') => {
    setMode(newMode);
    setIsLoading(false);
  }, []);

  const handleLogin = useCallback(async (email: string, password: string, opts?: { pairingKey?: string | null; figmaSource?: string | null }) => {
    setIsLoading(true);
    try {
      const loginData: LoginRequest = { email, password };

      // Add pairing_key if provided
      if (opts?.pairingKey) {
        loginData.pairing_key = opts.pairingKey;
      }

      const response = await authService.login(loginData);

      if (!response.access_token) {
        throw new Error(response.message || 'Invalid login response from server');
      }

      saveAccessToken(response.access_token);
      saveRefreshToken(response.refresh_token);
      (window as any).appAccessToken = response.access_token;
      const accessToken = response.access_token;

      // Update Redux state with user data and check onboarding status
      const userData = await dispatch(getUserProfile(accessToken)).unwrap();

      // Check if coming from Figma plugin
      // The pairing_key is the critical parameter for plugin authentication
      if (opts?.pairingKey) {
        closeDialog();
        const redirectUrl = `/redirect?pairing_key=${opts.pairingKey}&figma_source=${opts.figmaSource || 'desktop'}`;
        navigate(redirectUrl);
        return;
      }

      // Check if onboarding should be shown based on metadata.isOnBoardingShown flag
      const pathname = window.location.pathname;
      const isOnLandingPage = pathname === '/' || pathname === '/landing';
      // Check both camelCase and snake_case, and handle string values
      const isOnBoardingShownRaw = userData?.metadata?.isOnBoardingShown ??
        userData?.metadata?.is_onboarding_shown ??
        false;
      const isOnBoardingShown = typeof isOnBoardingShownRaw === 'string'
        ? isOnBoardingShownRaw.toLowerCase() === 'true'
        : Boolean(isOnBoardingShownRaw);
      // Only show onboarding if first-time user on landing page
      if (isOnBoardingShown === false && isOnLandingPage) {
        dispatch(setShowOnboarding(true));
        navigate('/onboarding');
      } else {
        // All other cases - navigate to home page
        // Update onboarding flag if needed
        if (isOnBoardingShown === false) {
          authService.updateOnboardingShown().catch((error) => {
            console.error('Failed to update onboarding shown flag:', error);
          });
        }
        // Don't redirect if user is on conversation page or came from conversation
        if (handleConversationRedirect()) {
          return;
        }
        navigate('/');
      }

      closeDialog();
    } catch (error: any) {
      const apiError = error as ApiError;
      throw new Error(apiError.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, closeDialog, navigate]);

  const handleRegister = useCallback(
    async (name: string, email: string, password: string, opts?: { fromChat?: boolean; pairingKey?: string | null; figmaSource?: string | null }) => {
      setIsLoading(true);
      try {
        const registerData: RegisterRequest = {
          email,
          password,
          full_name: name,
        };

        // Add pairing_key if provided
        if (opts?.pairingKey) {
          registerData.pairing_key = opts.pairingKey;
        }

        const response = await authService.register(registerData);

        if (!response.access_token) {
          throw new Error(response.message || 'Invalid registration response from server');
        }

        saveAccessToken(response.access_token);
        saveRefreshToken(response.refresh_token);
        (window as any).appAccessToken = response.access_token;
        const accessToken = response.access_token;

        // Update Redux state with user data and check onboarding status
        const userData = await dispatch(getUserProfile(accessToken)).unwrap();

        // Check if coming from Figma plugin
        // The pairing_key is the critical parameter for plugin authentication
        if (opts?.pairingKey) {
          closeDialog();
          const redirectUrl = `/redirect?pairing_key=${opts.pairingKey}&figma_source=${opts.figmaSource || 'desktop'}`;
          navigate(redirectUrl);
          return;
        }

        // Check if onboarding should be shown based on metadata.isOnBoardingShown flag
        const pathname = window.location.pathname;
        const isOnLandingPage = pathname === '/' || pathname === '/landing';
        // Check both camelCase and snake_case, and handle string values
        const isOnBoardingShownRaw = userData?.metadata?.isOnBoardingShown ??
          userData?.metadata?.is_onboarding_shown ??
          false;
        const isOnBoardingShown = typeof isOnBoardingShownRaw === 'string'
          ? isOnBoardingShownRaw.toLowerCase() === 'true'
          : Boolean(isOnBoardingShownRaw);
        // Only show onboarding if:
        // 1. First-time user (isOnBoardingShown === false)
        // 2. On landing page
        // 3. Not from chat (onboardingSource !== 'chat')
        const shouldShowOnboarding = isOnBoardingShown === false &&
          isOnLandingPage &&
          (onboardingSource === 'homepage' || onboardingSource === undefined);

        if (shouldShowOnboarding) {
          dispatch(setShowOnboarding(true));
          navigate('/onboarding');
        } else {
          // All other cases - navigate to home page
          // Update onboarding flag if needed
          if (isOnBoardingShown === false) {
            authService.updateOnboardingShown().catch((error) => {
              console.error('Failed to update onboarding shown flag:', error);
            });
          }
          // Don't redirect if user is on conversation page or came from conversation
          if (handleConversationRedirect()) {
            return;
          }
          navigate('/');
        }

        closeDialog();
      } catch (error: any) {
        const apiError = error as ApiError;
        throw new Error(apiError.message || 'Registration failed');
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, closeDialog, navigate, onboardingSource]
  );

  const handleGoogleAuth = useCallback(async (opts?: { pairingKey?: string | null; figmaSource?: string | null }) => {
    if (!googleIsLoaded) {
      throw new Error('Google authentication is not ready yet');
    }

    if (googleError) {
      throw new Error(googleError);
    }

    try {
      // Store plugin redirect parameters for use in success callback
      // The pairing_key is the critical parameter for plugin authentication
      console.log('handleGoogleAuth called with opts:', opts);
      if (opts?.pairingKey) {
        pluginRedirectParamsRef.current = { pairingKey: opts.pairingKey, figmaSource: opts.figmaSource };
        console.log('Stored pairing params in ref:', pluginRedirectParamsRef.current);
      } else {
        // Clear params if no pairingKey
        pluginRedirectParamsRef.current = {};
        console.log('No pairingKey provided, cleared ref');
      }

      setIsLoading(true);
      await signInWithGoogle();
      // Success is handled by the Google auth callback
    } catch (error: any) {
      console.error('Google authentication failed:', error);
      setIsLoading(false);
      throw error; // Re-throw to let the component handle the error display
    } finally {
      // Don't set loading to false here as it's handled by the callback
      setIsLoading(false);
    }
  }, [googleIsLoaded, googleError, signInWithGoogle]);

  const handleNavigateToForgotPassword = useCallback(() => {
    closeDialog();
    navigate('/forgot-password', { state: { from: 'authDialog' } });
  }, [closeDialog, navigate]);

  return {
    isOpen,
    mode,
    isLoading: isLoading || googleIsLoading || googleLoading,
    error: googleError || undefined,
    openLogin,
    openRegister,
    closeDialog,
    switchMode,
    handleLogin,
    handleRegister,
    handleGoogleAuth,
    handleNavigateToForgotPassword,
    googleIsLoaded,
    onboardingSource,
    setOnboardingSource,
  };
};
